import React, { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Building, Search, Eye, AlertTriangle } from 'lucide-react';

import PropertyDialog from '../dialogs/PropertyDialog';
import DeletePropertyDialog from '../dialogs/DeletePropertyDialog';
import StatusChip from '../StatusChip';

const PropertiesTab = ({ isLoading, setIsLoading, currentUser, showNotification, logAction }) => {
  // Property specific states
  const [propertySearchTerm, setPropertySearchTerm] = useState('');
  const [properties, setProperties] = useState([]);
  const [propertiesPagination, setPropertiesPagination] = useState({ page: 0, rowsPerPage: 10, count: 0 });
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [propertyDialogOpen, setPropertyDialogOpen] = useState(false);
  const [editPropertyData, setEditPropertyData] = useState({});
  const [deletePropertyDialogOpen, setDeletePropertyDialogOpen] = useState(false);
  const [propertyOwner, setPropertyOwner] = useState(null);

  // Animation variant
  const slideUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.5, 
        ease: "easeOut" 
      } 
    }
  };

  // Search properties
  const fetchProperties = async () => {
    if (!propertySearchTerm) return;
    
    setIsLoading(true);
    try {
      const response = await axios.get(`/api/admin/properties/search?term=${propertySearchTerm}&page=${propertiesPagination.page}&limit=${propertiesPagination.rowsPerPage}`);
      setProperties(response.data.properties);
      setPropertiesPagination({
        ...propertiesPagination,
        count: response.data.total
      });
    } catch (error) {
      console.error('Error al buscar propiedades:', error);
      showNotification('Error al buscar propiedades', 'error');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle property search
  const handlePropertySearch = (e) => {
    e.preventDefault();
    if (propertySearchTerm) {
      fetchProperties();
    }
  };
  
  // Change page or rows per page
  const handlePropertiesPaginationChange = (event, newPage) => {
    setPropertiesPagination({
      ...propertiesPagination,
      page: newPage
    });
    fetchProperties();
  };
  
  const handlePropertiesRowsPerPageChange = (event) => {
    setPropertiesPagination({
      ...propertiesPagination,
      rowsPerPage: parseInt(event.target.value, 10),
      page: 0
    });
    fetchProperties();
  };
  
  // View property details
  const handleViewProperty = async (property) => {
    setSelectedProperty(property);
    setEditPropertyData({ ...property });
    
    // Get owner information
    try {
      const response = await axios.get(`/api/admin/users/${property.id_vendedor}`);
      setPropertyOwner(response.data);
    } catch (error) {
      console.error('Error al obtener datos del propietario:', error);
      setPropertyOwner(null);
    }
    
    setPropertyDialogOpen(true);
  };

  // Save property changes
  const handleSaveProperty = async () => {
    setIsLoading(true);
    try {
      await axios.put(`/api/admin/properties/${selectedProperty.id}`, editPropertyData);
      
      showNotification('Propiedad actualizada correctamente', 'success');
      logAction(
        'PROPIEDAD_ACTUALIZADA',
        `Se ha actualizado la propiedad ${selectedProperty.titulo} (${selectedProperty.id})`,
        selectedProperty.id,
        'property'
      );
      
      setPropertyDialogOpen(false);
      if (propertySearchTerm) {
        fetchProperties();
      }
    } catch (error) {
      console.error('Error al actualizar propiedad:', error);
      showNotification(`Error al actualizar propiedad: ${error.response?.data?.message || error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Suspend property
  const handleSuspendProperty = async () => {
    setIsLoading(true);
    try {
      await axios.put(`/api/admin/properties/${selectedProperty.id}/suspend`);
      
      showNotification('Propiedad suspendida correctamente', 'success');
      logAction(
        'PROPIEDAD_SUSPENDIDA',
        `Se ha suspendido la propiedad ${selectedProperty.titulo} (${selectedProperty.id})`,
        selectedProperty.id,
        'property'
      );
      
      setPropertyDialogOpen(false);
      if (propertySearchTerm) {
        fetchProperties();
      }
    } catch (error) {
      console.error('Error al suspender propiedad:', error);
      showNotification(`Error al suspender propiedad: ${error.response?.data?.message || error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Delete property
  const handleDeleteProperty = async () => {
    setIsLoading(true);
    try {
      await axios.delete(`/api/admin/properties/${selectedProperty.id}`);
      
      showNotification('Propiedad eliminada correctamente', 'success');
      logAction(
        'PROPIEDAD_ELIMINADA',
        `Se ha eliminado la propiedad ${selectedProperty.titulo} (${selectedProperty.id})`,
        selectedProperty.id,
        'property'
      );
      
      setDeletePropertyDialogOpen(false);
      setPropertyDialogOpen(false);
      if (propertySearchTerm) {
        fetchProperties();
      }
    } catch (error) {
      console.error('Error al eliminar propiedad:', error);
      showNotification(`Error al eliminar propiedad: ${error.response?.data?.message || error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // View user details (property owner)
  const handleViewUser = (user) => {
    // Esta función debería ser implementada por un componente padre
    // que maneje la navegación entre pestañas
    showNotification('Funcionalidad no implementada', 'info');
  };

  return (
    <>
      <motion.div 
        className="bg-white rounded-xl shadow-lg overflow-hidden"
        variants={slideUp}
      >
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center gap-2 mb-4">
            <Building className="w-5 h-5 text-indigo-600" />
            <h2 className="text-xl font-semibold text-gray-800">Gestión de Propiedades</h2>
          </div>
          
          <form onSubmit={handlePropertySearch} className="flex flex-col md:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-indigo-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Buscar por ID, Título o Ubicación"
                value={propertySearchTerm}
                onChange={(e) => setPropertySearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-indigo-50/50 border border-indigo-100 rounded-lg text-gray-700 
                          placeholder-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
            <motion.button
              type="submit"
              className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-blue-600 text-white rounded-lg shadow-md"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              Buscar
            </motion.button>
          </form>
        </div>

        {properties.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-indigo-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-indigo-800 uppercase tracking-wider">ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-indigo-800 uppercase tracking-wider">Título</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-indigo-800 uppercase tracking-wider">Tipo</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-indigo-800 uppercase tracking-wider">Ubicación</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-indigo-800 uppercase tracking-wider">Precio</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-indigo-800 uppercase tracking-wider">Vendedor</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-indigo-800 uppercase tracking-wider">Estado</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-indigo-800 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {properties.map((property) => (
                  <tr key={property.id} className="hover:bg-indigo-50/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{property.id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{property.titulo}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{property.tipo}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {property.ubicacion?.substring(0, 20)}...
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {property.operacion === 'alquiler' ? 
                        `S/. ${property.precio_mensual}/mes` : 
                        `S/. ${property.precio_soles}`
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{property.id_vendedor}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusChip status={property.estado} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <motion.button
                        onClick={() => handleViewProperty(property)}
                        className="p-1.5 text-indigo-600 hover:bg-indigo-100 rounded-full transition-colors"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <Eye className="w-4 h-4" />
                      </motion.button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {propertySearchTerm && properties.length === 0 && (
          <div className="py-12 flex flex-col items-center justify-center">
            <AlertTriangle className="w-12 h-12 text-amber-400 mb-4" />
            <p className="text-lg font-medium text-gray-700 mb-1">No se encontraron propiedades</p>
            <p className="text-gray-500">Intenta con otro término de búsqueda</p>
          </div>
        )}

        {!propertySearchTerm && (
          <div className="py-12 flex flex-col items-center justify-center">
            <Search className="w-12 h-12 text-indigo-300 mb-4" />
            <p className="text-lg font-medium text-gray-700 mb-1">Busca propiedades para ver resultados</p>
            <p className="text-gray-500">Ingresa un ID, título o ubicación para comenzar</p>
          </div>
        )}

        {properties.length > 0 && (
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Mostrando {propertiesPagination.page * propertiesPagination.rowsPerPage + 1} a{' '}
              {Math.min((propertiesPagination.page + 1) * propertiesPagination.rowsPerPage, propertiesPagination.count)} de{' '}
              {propertiesPagination.count} propiedades
            </div>
            
            <div className="flex gap-2">
              <select
                value={propertiesPagination.rowsPerPage}
                onChange={(e) => handlePropertiesRowsPerPageChange(e)}
                className="px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value={5}>5 / página</option>
                <option value={10}>10 / página</option>
                <option value={25}>25 / página</option>
                <option value={50}>50 / página</option>
              </select>
              
              <div className="flex">
                <button
                  onClick={() => handlePropertiesPaginationChange(null, propertiesPagination.page - 1)}
                  disabled={propertiesPagination.page === 0}
                  className={`px-3 py-1 border ${
                    propertiesPagination.page === 0
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-indigo-600 hover:bg-indigo-50'
                  } rounded-l-lg transition-colors`}
                >
                  Anterior
                </button>
                <button
                  onClick={() => handlePropertiesPaginationChange(null, propertiesPagination.page + 1)}
                  disabled={propertiesPagination.page >= Math.ceil(propertiesPagination.count / propertiesPagination.rowsPerPage) - 1}
                  className={`px-3 py-1 border border-l-0 ${
                    propertiesPagination.page >= Math.ceil(propertiesPagination.count / propertiesPagination.rowsPerPage) - 1
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-indigo-600 hover:bg-indigo-50'
                  } rounded-r-lg transition-colors`}
                >
                  Siguiente
                </button>
              </div>
            </div>
          </div>
        )}
      </motion.div>
      
      {/* Property Dialogs */}
      <PropertyDialog 
        propertyDialogOpen={propertyDialogOpen}
        setPropertyDialogOpen={setPropertyDialogOpen}
        selectedProperty={selectedProperty}
        editPropertyData={editPropertyData}
        setEditPropertyData={setEditPropertyData}
        handleSaveProperty={handleSaveProperty}
        handleSuspendProperty={handleSuspendProperty}
        setDeletePropertyDialogOpen={setDeletePropertyDialogOpen}
        propertyOwner={propertyOwner}
        handleViewUser={handleViewUser}
      />
      
      <DeletePropertyDialog 
        deletePropertyDialogOpen={deletePropertyDialogOpen}
        setDeletePropertyDialogOpen={setDeletePropertyDialogOpen}
        selectedProperty={selectedProperty}
        handleDeleteProperty={handleDeleteProperty}
      />
    </>
  );
};

export default PropertiesTab;